import { Qfill } from '../js/qfill';
export var AllowedEvents;
(function (AllowedEvents) {
    AllowedEvents["redraw"] = "redraw";
    AllowedEvents["fill"] = "fill";
    AllowedEvents["mouseup"] = "mouseup";
    AllowedEvents["mousedown"] = "mousedown";
    AllowedEvents["mouseenter"] = "mouseenter";
    AllowedEvents["mouseleave"] = "mouseleave";
})(AllowedEvents || (AllowedEvents = {}));
export default class CanvasFreeDrawing {
    constructor(params) {
        const { canvas, width, height, backgroundColor = "#ffffff", lineWidth = 15, strokeColor = "#4d4d4d", disabled, showWarnings = false, maxSnapshots = 10, bucketToolTolerance = 10, bucketMode = true } = params;
        this.requiredParam(params, 'canvas');
        this.requiredParam(params, 'width');
        this.requiredParam(params, 'height');
        this.canvas = canvas;
        this.context = this.canvas.getContext('2d');
        this.width = width;
        this.height = height;
        this.maxSnapshots = maxSnapshots;
        this.snapshots = [];
        this.undos = [];
        this.positions = [];
        this.leftCanvasDrawing = false; // to check if user left the canvas drawing, on mouseover resume drawing
        this.isDrawing = false;
        this.isDrawingModeEnabled = true;
        this.imageRestored = false;
        this.lineWidth = lineWidth;
        this.strokeColor = strokeColor;
        this.bucketToolColor = strokeColor;
        this.bucketToolTolerance = bucketToolTolerance;
        this.isBucketToolEnabled = bucketMode;
        this.listenersList = [
            'mouseDown',
            'mouseMove',
            'mouseLeave',
            'mouseUp',
            'touchStart',
            'touchMove',
            'touchEnd',
        ];
        this.allowedEvents = this.getAllowedEvents();
        this.redrawCounter = 0;
        this.dispatchEventsOnceEvery = 0; // this may become something like: [{event, counter}]
        // // initialize events
        this.events = {
            redrawEvent: new Event('cfd_redraw'),
            fillEvent: new Event('cfd_fill'),
            mouseUpEvent: new Event('cfd_mouseup'),
            mouseDownEvent: new Event('cfd_mousedown'),
            mouseEnterEvent: new Event('cfd_mouseenter'),
            mouseLeaveEvent: new Event('cfd_mouseleave'),
            touchStartEvent: new Event('cfd_touchstart'),
            touchEndEvent: new Event('cfd_touchend'),
        };
        this.bindings = {
            mouseDown: this.mouseDown.bind(this),
            mouseMove: this.mouseMove.bind(this),
            mouseLeave: this.mouseLeave.bind(this),
            mouseUp: this.mouseUp.bind(this),
            mouseUpDocument: this.mouseUpDocument.bind(this),
            touchStart: this.touchStart.bind(this),
            touchMove: this.touchMove.bind(this),
            touchEnd: this.touchEnd.bind(this),
        };
        this.touchIdentifier = undefined;
        this.previousX = undefined;
        this.previousY = undefined;
        this.showWarnings = showWarnings;
        this.storeSnapshot();
        if (!disabled)
            this.enableDrawingMode();
    }
    requiredParam(object, param) {
        if (!object || !object[param]) {
            throw new Error(`${param} is required`);
        }
    }
    logWarning(...args) {
        if (this.showWarnings)
            console.warn(...args);
    }
    addListeners() {
        this.listenersList.forEach((event) => {
            this.canvas.addEventListener(event.toLowerCase(), this.bindings[event]);
        });
        document.addEventListener('mouseup', this.bindings.mouseUpDocument);
    }
    removeListeners() {
        this.listenersList.forEach((event) => {
            this.canvas.removeEventListener(event.toLowerCase(), this.bindings[event]);
        });
        document.removeEventListener('mouseup', this.bindings.mouseUpDocument);
    }
    getAllowedEvents() {
        const events = [];
        for (const event in AllowedEvents) {
            events.push(event);
        }
        return events;
    }
    enableDrawingMode() {
        this.isDrawingModeEnabled = true;
        this.addListeners();
        this.toggleCursor();
        return this.isDrawingModeEnabled;
    }
    disableDrawingMode() {
        this.isDrawingModeEnabled = false;
        this.removeListeners();
        this.toggleCursor();
        return this.isDrawingModeEnabled;
    }
    mouseDown(event) {
        if (event.button !== 0)
            return;
        this.drawPoint(event.offsetX, event.offsetY);
    }
    mouseMove(event) {
        this.drawLine(event.offsetX, event.offsetY, event);
    }
    touchStart(event) {
        if (event.changedTouches.length > 0) {
            const { pageX, pageY, identifier } = event.changedTouches[0];
            const x = pageX - this.canvas.offsetLeft;
            const y = pageY - this.canvas.offsetTop;
            this.touchIdentifier = identifier;
            this.drawPoint(x, y);
        }
    }
    touchMove(event) {
        if (event.changedTouches.length > 0) {
            const { pageX, pageY, identifier } = event.changedTouches[0];
            const x = pageX - this.canvas.offsetLeft;
            const y = pageY - this.canvas.offsetTop;
            // check if is multi touch, if it is do nothing
            if (identifier != this.touchIdentifier)
                return;
            this.previousX = x;
            this.previousY = y;
            this.drawLine(x, y, event);
        }
    }
    touchEnd() {
        this.handleEndDrawing();
        this.canvas.dispatchEvent(this.events.touchEndEvent);
    }
    mouseUp() {
        this.handleEndDrawing();
        this.canvas.dispatchEvent(this.events.mouseUpEvent);
    }
    mouseUpDocument() {
        this.leftCanvasDrawing = false;
    }
    mouseLeave() {
        if (this.isDrawing)
            this.leftCanvasDrawing = true;
        this.isDrawing = false;
        this.canvas.dispatchEvent(this.events.mouseLeaveEvent);
    }
    mouseEnter() {
        this.canvas.dispatchEvent(this.events.mouseEnterEvent);
    }
    handleEndDrawing() {
        this.isDrawing = false;
        this.storeSnapshot();
    }
    drawPoint(x, y) {
        if (this.isBucketToolEnabled) {
            const qfill = Qfill(this.context.getImageData(0, 0, this.canvas.width, this.canvas.height));
            qfill.fill(this.bucketToolColor, x, y, this.bucketToolTolerance);
            this.context.putImageData(qfill.imageData, 0, 0);
        }
        else {
            this.isDrawing = true;
            this.storeDrawing(x, y, false);
            this.canvas.dispatchEvent(this.events.mouseDownEvent);
            this.handleDrawing();
        }
    }
    drawLine(x, y, event) {
        if (this.leftCanvasDrawing) {
            this.leftCanvasDrawing = false;
            if (event instanceof MouseEvent) {
                this.mouseDown(event);
            }
            else if (event instanceof TouchEvent) {
                this.touchEnd();
            }
        }
        if (this.isDrawing) {
            this.storeDrawing(x, y, true);
            this.handleDrawing(this.dispatchEventsOnceEvery);
        }
    }
    handleDrawing(dispatchEventsOnceEvery) {
        this.context.lineJoin = 'round';
        const positions = [[...this.positions].pop()];
        positions.forEach((position) => {
            if (position && position[0] && position[0].strokeColor) {
                this.context.strokeStyle = position[0].strokeColor;
                this.context.lineWidth = position[0].lineWidth;
                this.draw(position);
            }
        });
        if (!dispatchEventsOnceEvery) {
            this.canvas.dispatchEvent(this.events.redrawEvent);
        }
        else if (this.redrawCounter % dispatchEventsOnceEvery === 0) {
            this.canvas.dispatchEvent(this.events.redrawEvent);
        }
        this.undos = [];
        this.redrawCounter += 1;
    }
    draw(position) {
        position.forEach(({ x, y, moving }, i) => {
            this.context.beginPath();
            if (moving && i) {
                this.context.moveTo(position[i - 1]['x'], position[i - 1]['y']);
            }
            else {
                this.context.moveTo(x - 1, y);
            }
            this.context.lineTo(x, y);
            this.context.closePath();
            this.context.stroke();
        });
    }
    toValidColor(color) {
        if (Array.isArray(color) && color.length === 4)
            return color;
        if (Array.isArray(color) && color.length === 3) {
            const validColor = [...color];
            validColor.push(255);
            return validColor;
        }
        else {
            this.logWarning('Color is not valid!\n' +
                'It must be an array with RGB values:  [0-255, 0-255, 0-255]');
            return [0, 0, 0, 255];
        }
    }
    rgbaFromArray(a) {
        return `rgba(${a[0]},${a[1]},${a[2]},${a[3]})`;
    }
    setDimensions() {
        this.canvas.height = this.height;
        this.canvas.width = this.width;
    }
    toggleCursor() {
        this.canvas.style.cursor = this.isDrawingModeEnabled ? 'crosshair' : 'auto';
    }
    storeDrawing(x, y, moving) {
        if (moving) {
            const lastIndex = this.positions.length - 1;
            this.positions[lastIndex].push({
                x,
                y,
                moving,
                lineWidth: this.lineWidth,
                strokeColor: this.strokeColor,
                isBucket: false,
            });
        }
        else {
            this.positions.push([
                {
                    x,
                    y,
                    isBucket: false,
                    moving,
                    lineWidth: this.lineWidth,
                    strokeColor: this.strokeColor,
                },
            ]);
        }
    }
    storeSnapshot() {
        const imageData = this.getCanvasSnapshot();
        this.snapshots.push(imageData);
        if (this.snapshots.length > this.maxSnapshots) {
            this.snapshots = this.snapshots.splice(-Math.abs(this.maxSnapshots));
        }
    }
    getCanvasSnapshot() {
        return this.context.getImageData(0, 0, this.width, this.height);
    }
    restoreCanvasSnapshot(imageData) {
        this.context.putImageData(imageData, 0, 0);
    }
    // Public APIs
    on(params, callback) {
        const { event, counter } = params;
        this.requiredParam(params, 'event');
        if (this.allowedEvents.includes(event)) {
            if (event === 'redraw' && counter && Number.isInteger(counter)) {
                this.dispatchEventsOnceEvery = counter;
            }
            this.canvas.addEventListener('cfd_' + event, () => callback());
        }
        else {
            this.logWarning(`This event is not allowed: ${event}`);
        }
    }
    setLineWidth(px) {
        this.lineWidth = px;
    }
    setBackground(color, save = true) {
        const validColor = color;
        if (validColor) {
            if (save)
                this.backgroundColor = validColor;
            this.context.fillStyle = validColor;
            this.context.fillRect(0, 0, this.width, this.height);
        }
    }
    setDrawingColor(color) {
        this.configBucketTool({ color });
        this.setStrokeColor(color);
    }
    getDrawingColor() {
        return this.strokeColor;
    }
    setStrokeColor(color) {
        this.strokeColor = color; //default direct color
    }
    setBucketColor(color) {
        this.configBucketTool({ color });
    }
    configBucketTool(params) {
        const { color = null, tolerance = null } = params;
        if (color)
            this.bucketToolColor = color;
        if (tolerance && tolerance > 0) {
            this.bucketToolTolerance = tolerance > 100 ? 100 : tolerance;
        }
    }
    toggleBucketTool() {
        return (this.isBucketToolEnabled = !this.isBucketToolEnabled);
    }
    setBucketMode(mode = true) {
        this.isBucketToolEnabled = mode;
    }
    toggleDrawingMode() {
        return this.isDrawingModeEnabled
            ? this.disableDrawingMode()
            : this.enableDrawingMode();
    }
    clear() {
        this.context.clearRect(0, 0, this.width, this.height);
        this.positions = [];
        this.imageRestored = false;
        if (this.backgroundColor)
            this.setBackground(this.backgroundColor);
        this.handleEndDrawing();
    }
    save() {
        return this.canvas.toDataURL();
    }
    restore(backup, callback) {
        const image = new Image();
        image.src = backup;
        image.onload = () => {
            this.imageRestored = true;
            this.context.drawImage(image, 0, 0);
            if (typeof callback === 'function')
                callback();
        };
    }
    undo() {
        const lastSnapshot = this.snapshots[this.snapshots.length - 1];
        const goToSnapshot = this.snapshots[this.snapshots.length - 2];
        if (goToSnapshot) {
            this.restoreCanvasSnapshot(goToSnapshot);
            this.snapshots.pop();
            this.undos.push(lastSnapshot);
            this.undos = this.undos.splice(-Math.abs(this.maxSnapshots));
            this.imageRestored = true;
        }
        else {
            this.logWarning('There are no more undos left.');
        }
    }
    redo() {
        if (this.undos.length > 0) {
            const lastUndo = this.undos.pop();
            if (lastUndo) {
                this.restoreCanvasSnapshot(lastUndo);
                this.snapshots.push(lastUndo);
                this.snapshots = this.snapshots.splice(-Math.abs(this.maxSnapshots));
            }
        }
        else {
            this.logWarning('There are no more redo left.');
        }
    }
}
